/**
 * The landing page — what a signed-out visitor sees at `/`.
 *
 * One rule shaped every section: **nothing on this page is a claim, everything is the game.**
 * The hero is the arena renderer flying over a real map with a squad on it (`landing-scene.ts`),
 * not a screenshot of it. The arsenal cards
 * read their bars from `shared/src/weapons.ts` — the same numbers the server resolves a shot
 * with. The maps are drawn from their own geometry (`blueprint.ts`). The operators are the
 * catalogue. The netcode figures are the constants the protocol is built on. When a designer
 * retunes the SMG or someone adds a map, this page is already right.
 *
 * Structure: a fixed top bar, a full-viewport hero, then Modes → Arsenal → Maps → Operators →
 * Under the hood → Questions → a closing call to action → footer with the CMS legal documents.
 * Sections reveal on scroll (IntersectionObserver; instant when unavailable or under reduced
 * motion).
 *
 * Mobile-first per CLAUDE.md: everything stacks, strips scroll horizontally with snap, tap
 * targets ≥44px, `dvh` for the hero, safe-area padding on the fixed bar.
 *
 * Two additions worth their own note:
 *
 * **The drawer.** The desktop nav is `hidden md:flex`, so under 768px the top bar used to be a
 * wordmark and two buttons — a visitor on a phone could reach no section of the page at all.
 * The burger and its slide-down drawer carry the same links, the burger is a real 44x44 control,
 * and the drawer closes on a link, on Escape and on a tap outside, because a nav that traps you
 * is worse than no nav. It animates its height in and closes instantly: an exit transition
 * needs a timer that would outlive `renderLanding`'s teardown, and nobody misses it.
 *
 * **The FAQ.** The questions are CMS rows (`GET /cms/faq`), not copy in this file, so support
 * answers them once and both this section and `/faq` are right. It renders as an accordion with
 * one panel open at a time, holds the first eight and links to the full page when there are
 * more. An empty list or a failed fetch removes the section outright — a marketing page never
 * shows an error, the same rule the footer's legal list already follows below.
 *
 * `landingTopBar` and `landingFooter` are exported because the content pages (/faq, /news,
 * /guide) wear the same chrome. Neither may assume it is on the landing page: the bar's
 * in-page anchors navigate home to the hash when the target section is not in the document.
 */
import { WEAPONS, type WeaponDefinition } from '../../../../shared/src/weapons.js';
import { SKIN_LIST, type SkinDefinition } from '../../../../shared/src/cosmetics.js';
import { MAX_REWIND_MS, SNAPSHOT_RATE, TICK_RATE } from '../../../../shared/src/protocol.js';
import type { MapDefinition } from '../../../../shared/src/map/definition.js';
import { CLIENT_MAPS } from '../arena/map-asset.js';
import type { LandingSceneHandle } from './landing-scene.js';
import { api } from '../../core/network/api';
import { authStore } from '../../core/state/auth-store';
import { icon, openModal, plateChrome, type IconName, type PlateChromeOptions } from '../shell/ui';
import {
  fetchCompanyInfo,
  fetchLegalPage,
  fetchLegalPages,
  fillCompanyVars,
  sortLegalPages,
  type CompanyInfo,
  type LegalPageMeta,
} from '../shell/legal-api';
import { blueprintSvg, mapFootprintMeters } from './blueprint';
import { socialIconUrl, socialPlatformKey } from '../social-platforms';
import { t } from '../i18n';

type Navigate = (to: string) => void;

const FLOAT_SHADOW = '[text-shadow:0_2px_10px_rgba(0,0,0,0.9),0_0_2px_rgba(0,0,0,0.9)]';

/**
 * Rendered figures for the netcode section, from the protocol itself.
 *
 * Figures and units are data; the prose is a catalogue key, never a `t()` call. A `t()` in a
 * module-level const runs once at import and freezes whatever locale booted, and `main.ts`
 * remounts the page on `setLocale` rather than re-evaluating this module — so both consumers
 * (`netcodeSection` here, the guide's netcode section) resolve these while building the node.
 */
export const NETCODE_FACTS = [
  { value: `${TICK_RATE}`, unit: 'Hz', labelKey: 'landing.netcode.simulationLabel', noteKey: 'landing.netcode.simulationNote' },
  { value: `${SNAPSHOT_RATE}`, unit: 'Hz', labelKey: 'landing.netcode.snapshotsLabel', noteKey: 'landing.netcode.snapshotsNote' },
  { value: `${MAX_REWIND_MS}`, unit: 'ms', labelKey: 'landing.netcode.rewindLabel', noteKey: 'landing.netcode.rewindNote' },
  { value: '12', unit: 'B', labelKey: 'landing.netcode.inputLabel', noteKey: 'landing.netcode.inputNote' },
] as const;

export interface ModeCard {
  id: string;
  nameKey: string;
  figure: string;
  figureLabelKey: string;
  rows: [labelKey: string, valueKey: string][];
  icon: IconName;
}

/**
 * The three ways to play, stated as rules rather than adjectives.
 *
 * Ids, figures and icons are data. Every word is a catalogue key resolved by whoever renders the
 * card — this array is module-level, and a `t()` here would be evaluated once at import and hold
 * the boot locale forever (`main.ts` remounts on `setLocale`, it does not re-run module bodies).
 */
export const MODE_CARDS: ModeCard[] = [
  {
    id: 'deathmatch',
    nameKey: 'landing.modes.deathmatch.name',
    figure: '10:00',
    figureLabelKey: 'landing.modes.deathmatch.figureLabel',
    rows: [
      ['landing.modes.deathmatch.teams', 'landing.modes.deathmatch.teamsValue'],
      ['landing.modes.deathmatch.death', 'landing.modes.deathmatch.deathValue'],
      ['landing.modes.deathmatch.win', 'landing.modes.deathmatch.winValue'],
      ['landing.modes.deathmatch.stakes', 'landing.modes.deathmatch.stakesValue'],
    ],
    icon: 'play',
  },
  {
    id: 'competitive',
    nameKey: 'landing.modes.competitive.name',
    figure: '12',
    figureLabelKey: 'landing.modes.competitive.figureLabel',
    rows: [
      ['landing.modes.competitive.round', 'landing.modes.competitive.roundValue'],
      ['landing.modes.competitive.objective', 'landing.modes.competitive.objectiveValue'],
      ['landing.modes.competitive.halftime', 'landing.modes.competitive.halftimeValue'],
      ['landing.modes.competitive.stakes', 'landing.modes.competitive.stakesValue'],
    ],
    icon: 'bomb',
  },
  {
    id: 'practice',
    nameKey: 'landing.modes.practice.name',
    figure: '5v5',
    figureLabelKey: 'landing.modes.practice.figureLabel',
    rows: [
      ['landing.modes.practice.rules', 'landing.modes.practice.rulesValue'],
      ['landing.modes.practice.bots', 'landing.modes.practice.botsValue'],
      ['landing.modes.practice.queue', 'landing.modes.practice.queueValue'],
      ['landing.modes.practice.stakes', 'landing.modes.practice.stakesValue'],
    ],
    icon: 'tools',
  },
];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function link(href: string, label: string, className: string, navigate: Navigate): HTMLAnchorElement {
  const a = el('a', className, label);
  a.href = href;
  a.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(href);
  });
  return a;
}

/**
 * The five in-page sections, in the order they appear. These scroll; they never navigate.
 *
 * A function, not a `const`: a module-level `t()` runs once at import and would freeze whatever
 * locale was active then into the array, and `main.ts` remounts the page rather than repainting
 * it, so the labels have to be read while the nav is being built.
 */
const navSections = (): [href: string, label: string][] => [
  ['#modes', t('nav.modes')],
  ['#arsenal', t('nav.arsenal')],
  ['#maps', t('nav.maps')],
  ['#operators', t('nav.operators')],
  ['#netcode', t('nav.netcode')],
];

/** The content routes, which are real SPA pages of their own. */
const navRoutes = (): [href: string, label: string][] => [
  ['/faq', t('nav.faq')],
  ['/news', t('nav.news')],
  ['/guide', t('nav.guide')],
];

/**
 * A link to a section of the landing page.
 *
 * Two things it has to get right. It `stopPropagation()`s, because `main.ts` intercepts every
 * same-origin anchor click at the document and routes `pathname + search` — a hash link would
 * be read as a navigation to the page we are already on and remount it under the scroll.
 * And when the section is not in this document — the same bar is mounted on /faq, /news and
 * /guide — it navigates to `/` plus the hash instead of quietly doing nothing.
 */
function sectionAnchor(href: string, label: string, className: string, navigate: Navigate, onDone?: () => void): HTMLAnchorElement {
  const a = el('a', className, label);
  a.href = href;
  a.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else navigate(`/${href}`);
    onDone?.();
  });
  return a;
}

/** An anchor dressed as a rendered key — the same control the shell's buttons are. */
function plateLink(href: string, label: string | Node, opts: PlateChromeOptions, navigate: Navigate): HTMLAnchorElement {
  const a = document.createElement('a');
  a.href = href;
  plateChrome(a, label, { ...opts, className: `no-underline ${opts.className ?? ''}` });
  a.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(href);
  });
  return a;
}

/** The wordmark as DOM — the same construction the rendered logo asset uses, so they match. */
export function wordmark(sizeClass: string, intro = false): HTMLElement {
  // `--font-wordmark`, not `font-heading`: this is a drawn lockup, not text set in the product's
  // face. The bar sits at 58% of the cap height and is skewed -12° to match Oswald's italic, and
  // `logo-blackline.png` — the rendered asset on the arena loading screen and in the emails —
  // was shot from this exact construction. Following the display face put the bar through the
  // wrong part of the letters and made the DOM wordmark disagree with the image of itself.
  const mark = el('span', `relative inline-block [font-family:var(--font-wordmark)] font-bold italic uppercase leading-none tracking-[0.02em] whitespace-nowrap ${sizeClass} ${intro ? 'wm-intro' : ''}`);
  mark.dataset.slot = 'wordmark';
  const black = el('span', 'text-[#f4f4f2]', 'Black ');
  const line = el('span', 'text-cyan', 'Line');
  const bar = el('span', 'absolute left-[-0.06em] right-[-0.06em] top-[58%] h-[0.075em] bg-cyan skew-x-[-12deg] pointer-events-none');
  bar.setAttribute('aria-hidden', 'true');
  mark.append(black, line, bar);
  return mark;
}

const CTA_PRIMARY: PlateChromeOptions = { variant: 'gold', size: 'lg', className: 'text-[16px] tracking-[0.12em]' };
const CTA_GHOST: PlateChromeOptions = { variant: 'secondary', size: 'lg', className: 'text-[15px] tracking-[0.12em]' };
const HERO_PRIMARY: PlateChromeOptions = { variant: 'gold', size: 'xl', className: 'text-[18px] tracking-[0.12em]' };

/** A painted key's label: the picture first, then the word. */
function ctaLabel(art: IconName, text: string, size = 28, float = false): DocumentFragment {
  const label = document.createDocumentFragment();
  label.append(icon(art, { size, className: float ? 'kit-float' : '' }), document.createTextNode(text));
  return label;
}

const SECTION = 'relative px-5 sm:px-8 lg:px-14 xl:px-24 py-20 lg:py-28';
const H2 = 'kit-title font-heading uppercase text-[22px] sm:text-[32px] lg:text-[42px] m-0';
const LEDE = 'text-[15px] sm:text-[17px] leading-relaxed text-text-dim max-w-[60ch] m-0';

/**
 * A section's title: the hazard-capped plate (`kit-header`) with the section's picture on it. The
 * plate hugs its title (`w-fit`) and grows with the viewport, so a 320px phone and a 1440px
 * desktop carry the same plate in proportion.
 */
function titlePlate(art: IconName, heading: HTMLElement, className = ''): HTMLElement {
  const plate = el('div', `kit-header w-fit max-w-full [--kh:56px] sm:[--kh:68px] lg:[--kh:80px] ${className}`);
  plate.append(icon(art, { size: 56, className: 'w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 kit-float' }), heading);
  return plate;
}

/** A content page's title: the same plate as a section's, one size up. */
export function pageTitle(art: IconName, heading: HTMLElement): HTMLElement {
  const plate = el('div', 'kit-header w-fit max-w-full [--kh:60px] sm:[--kh:76px]');
  plate.append(icon(art, { size: 56, className: 'w-10 h-10 sm:w-12 sm:h-12 kit-float' }), heading);
  return plate;
}

function sectionHead(art: IconName, title: string, lede?: string, align: 'left' | 'right' = 'left'): HTMLElement {
  const head = el('div', `reveal flex flex-col gap-4 mb-10 lg:mb-14 ${align === 'right' ? 'lg:items-end lg:text-right' : ''}`);
  head.append(titlePlate(art, el('h2', H2, title)));
  if (lede) head.append(el('p', LEDE, lede));
  return head;
}

// ── Hero ────────────────────────────────────────────────────────────────────────────────────


/**
 * The hero scene chunk, fetched at most once.
 *
 * `main.ts` starts this while `site-info` and the locale are still in flight — about a megabyte
 * of JS (Three, GLTFLoader, the arena renderer) that would otherwise queue behind the whole
 * boot and only start when the hero mounts. Memoised because the two callers must share one
 * download *and* one module evaluation: a second `import()` of the same specifier is deduped by
 * the module registry anyway, but holding the promise makes that a property of this file rather
 * than a trusted assumption about the loader, and gives `mountHero` the already-resolved module
 * on a warm navigation.
 */
let scenePreload: Promise<typeof import('./landing-scene.js')> | null = null;
export function preloadLandingScene(): Promise<typeof import('./landing-scene.js')> {
  scenePreload ??= import('./landing-scene.js');
  return scenePreload;
}

function mountHero(
  navigate: Navigate,
  reducedMotion: boolean,
): { node: HTMLElement; scene: LandingSceneHandle | null; ready: Promise<void>; showStill: () => void } {
  const hero = el('section', 'relative min-h-[var(--screen-h)] flex flex-col justify-end overflow-hidden');
  hero.id = 'top';

  const stage = el('div', 'absolute inset-0');
  stage.dataset.slot = 'hero-stage';
  hero.append(stage);

  /**
   * The still is a FALLBACK and is inserted only when asked for — it must never sit under a
   * live hero that is about to arrive.
   *
   * It did, briefly, on the reasoning that an empty stage is worse than a picture. That was
   * wrong for one reason nobody checked: `auth-keyart.jpg` is a shot of the **lobby** scene
   * (`keyart-harness.ts` frames `mountLobbyScene`) — a soldier in a container yard — while the
   * hero is an aerial over Dune II. Two unrelated pictures, cross-fading into each other right
   * as the page was revealed — a loader that still lets you watch the scene change is not a
   * loader. The fix is not a better fade, it is one picture.
   *
   * So on `/` with WebGL the stage stays empty behind the loading screen, which is covering it
   * anyway, and the first thing the visitor ever sees in the hero is the finished scene. The
   * still is for the paths where there will be no scene: no WebGL, a failed chunk, or the
   * splash's cap firing first — `main.ts` asks for it in that last case.
   */
  let still: HTMLImageElement | null = null;
  const showStill = (): void => {
    if (still || !stage.isConnected) return;
    still = el('img', 'absolute inset-0 w-full h-full object-cover');
    still.dataset.slot = 'hero-keyart';
    still.src = '/game/ui/auth-keyart.jpg';
    still.alt = '';
    still.setAttribute('aria-hidden', 'true');
    // First child, so a canvas that arrives later draws over it rather than under it.
    stage.prepend(still);
  };

  // Live scene, guarded: jsdom and very old browsers have no WebGL — the hero then falls back
  // to the rendered key art, so it never shows a hole.
  const canvasOk = typeof HTMLCanvasElement !== 'undefined' && !!document.createElement('canvas').getContext;
  const webgl = canvasOk && (() => {
    try {
      const probe = document.createElement('canvas');
      return !!(probe.getContext('webgl2') ?? probe.getContext('webgl'));
    } catch {
      return false;
    }
  })();

  // The scene module carries Three, the arena renderer and every bundled map's JSON
  // (`CLIENT_MAPS`) — none of which the landing's first paint should wait on, so it is a
  // dynamic import, and `main.ts` starts it during the boot's first fetches
  // (`preloadLandingScene`) so the megabyte overlaps them instead of queueing behind them.
  // `disposed` covers a visitor who navigates away before it resolves.
  let disposed = false;
  let live: LandingSceneHandle | null = null;
  let scene: LandingSceneHandle | null = null;
  // Resolved when the hero is as good as it is going to get: a rendered scene frame, or the
  // key art on any path where there will never be one. The splash gates the reveal on this, so
  // every branch has to settle it — an unresolved promise here would hold the splash up to its
  // cap for no reason.
  let settleReady: () => void = () => {};
  const ready = new Promise<void>((resolve) => {
    settleReady = resolve;
  });
  if (webgl) {
    scene = {
      // Placeholder handle: the real one arrives with the module. `ready` is this hero's, not
      // the scene's, because it also answers for the paths where no scene ever mounts.
      ready,
      dispose(): void {
        disposed = true;
        live?.dispose();
        live = null;
        settleReady();
      },
    };
    // Started at once, not on idle: the boot splash now waits for `ready` before revealing the
    // page (2026-09-10: keep the live scene, hold the loader until it has rendered), so every millisecond this defers is a
    // millisecond the visitor spends watching the loading bar. `main.ts` kicks the same memoised
    // import before its first fetch, so the download is usually already in flight by now.
    void preloadLandingScene()
      .then((module) => {
        if (disposed) return;
        live = module.mountLandingScene(stage, { still: reducedMotion, parallax: !reducedMotion });
        void live.ready.then(() => {
          // No cross-fade: if a still is present at all, this is a path where the loader has
          // already revealed the page, and swapping the picture under the visitor is the defect
          // this rewrite removes. Drop it instead — the canvas is above it and identical to what
          // it will keep drawing.
          still?.remove();
          still = null;
          settleReady();
        });
      })
      .catch((error: unknown) => {
        console.warn('[landing] hero scene failed to mount; showing key art', error);
        showStill();
        settleReady();
      });
  } else {
    // No WebGL: the still *is* the hero, it is there from the first frame, and nothing will
    // ever replace it.
    showStill();
    settleReady();
  }

  // Grade: darker at the left and bottom where the copy sits, clear at the figure.
  hero.append(
    el('div', 'absolute inset-0 pointer-events-none bg-[linear-gradient(90deg,rgba(10,11,13,0.92)_0%,rgba(10,11,13,0.55)_42%,rgba(10,11,13,0.05)_75%)]'),
    el('div', 'absolute inset-0 pointer-events-none bg-[linear-gradient(180deg,rgba(10,11,13,0.55)_0%,rgba(10,11,13,0)_30%,rgba(10,11,13,0.2)_70%,rgba(10,11,13,0.95)_100%)]'),
  );

  const copy = el('div', `relative z-10 px-5 sm:px-8 lg:px-14 xl:px-24 pb-28 lg:pb-32 pt-32 flex flex-col gap-6 max-w-[1100px] ${FLOAT_SHADOW}`);
  const title = el('h1', 'm-0');
  title.append(wordmark('text-[clamp(64px,14vw,168px)]', !reducedMotion));
  const tagline = el('p', 'reveal kit-title font-heading uppercase text-[clamp(20px,3.2vw,38px)] leading-[1.05] max-w-[22ch] m-0');
  tagline.textContent = t('landing.hero.tagline');
  const ctas = el('div', 'reveal flex flex-wrap items-center gap-4 pt-2');
  ctas.append(
    plateLink('/register', ctaLabel('play', t('landing.hero.playFree'), 40, true), HERO_PRIMARY, navigate),
    plateLink('/login', ctaLabel('shield', t('landing.hero.signIn')), CTA_GHOST, navigate),
  );
  copy.append(title, tagline, ctas);
  hero.append(copy);

  return { node: hero, scene, ready, showStill };
}

// ── Modes ───────────────────────────────────────────────────────────────────────────────────

function modesSection(): HTMLElement {
  const section = el('section', `${SECTION} bg-bg`);
  section.id = 'modes';
  section.append(sectionHead('trophy', t('landing.modes.title'), t('landing.modes.lede')));
  const grid = el('div', 'grid gap-5 md:grid-cols-3');
  MODE_CARDS.forEach((mode, index) => {
    const card = el('article', `reveal tilt-card kit-plate [--kp-rim:22px] [--kp-pad:26px_24px] flex flex-col gap-5 [transition-delay:${index * 90}ms]`);
    card.dataset.mode = mode.id;
    const head = el('div', 'flex items-center gap-4');
    head.append(
      icon(mode.icon, { size: 96, className: 'w-20 h-20 lg:w-24 lg:h-24 kit-float [filter:drop-shadow(0_8px_10px_rgba(0,0,0,0.6))]' }),
      el('h3', 'kit-title font-heading uppercase text-[26px] m-0', t(mode.nameKey)),
    );
    const figure = el('div', 'kit-well flex items-baseline gap-3 px-4 py-2');
    figure.append(
      el('span', 'kit-title font-heading text-[56px] leading-none tabular-nums', mode.figure),
      el('span', 'font-heading uppercase tracking-[0.18em] text-[12px] text-text-dim', t(mode.figureLabelKey)),
    );
    const rows = el('dl', 'flex flex-col gap-2 m-0 text-[14px]');
    for (const [labelKey, valueKey] of mode.rows) {
      const row = el('div', 'kit-well flex items-baseline justify-between gap-4 px-3 py-2');
      row.append(
        el('dt', 'font-heading uppercase tracking-[0.16em] text-[11px] text-text-dim shrink-0', t(labelKey)),
        el('dd', 'm-0 text-text text-right', t(valueKey)),
      );
      rows.append(row);
    }
    card.append(head, figure, rows);
    attachTilt(card, 4);
    grid.append(card);
  });
  section.append(grid);
  return section;
}

/**
 * Pointer tilt: the card leans toward the cursor by up to `maxDeg`, in 3D. Only where a pointer
 * can hover — touch gets no tilt, reduced motion gets none — and only as an inline transform,
 * so the card's own reveal transition still owns opacity.
 */
function attachTilt(card: HTMLElement, maxDeg: number): void {
  if (typeof matchMedia !== 'function') return;
  if (!matchMedia('(hover: hover)').matches || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  card.addEventListener('pointermove', (event) => {
    const rect = card.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width - 0.5;
    const py = (event.clientY - rect.top) / rect.height - 0.5;
    card.style.transform = `perspective(900px) rotateX(${(-py * maxDeg).toFixed(2)}deg) rotateY(${(px * maxDeg).toFixed(2)}deg) translateY(-2px)`;
  });
  card.addEventListener('pointerleave', () => { card.style.transform = ''; });
}

// ── Arsenal ─────────────────────────────────────────────────────────────────────────────────

/** Guns only — the grenade is utility and has no rate of fire to draw. */
export function arsenalWeapons(): WeaponDefinition[] {
  // Guns only: a thrown weapon has no barrel to show. By the flag, not the name — the third
  // roster (2026-09-08) brought three more throwables, none of them named 'grenade'.
  return WEAPONS.filter((weapon) => weapon.thrown !== true);
}

function statBar(art: IconName, label: string, value: number, max: number, display: string, delayMs: number): HTMLElement {
  const row = el('div', 'flex items-center gap-2');
  row.title = label;
  const name = el('span', 'sr-only', label);
  const track = el('div', 'kit-bar [--kh:20px] flex-1 min-w-0');
  const fill = el('div', 'kit-bar-fill');
  // Starts empty; `fillBars` sets the real width when the card reveals, so the bars are seen
  // filling to their value rather than sitting there.
  fill.dataset.target = `${Math.max(3, Math.round((value / max) * 100))}%`;
  fill.style.width = '0%';
  fill.style.transitionDuration = '900ms';
  fill.style.transitionDelay = `${delayMs}ms`;
  track.append(fill);
  row.append(icon(art, { size: 28 }), name, track, el('span', 'kit-label font-heading text-[12px] tabular-nums w-[4.5rem] text-right shrink-0', display));
  return row;
}

/** Called once by the reveal observer for any element carrying bars. */
function fillBars(scope: HTMLElement): void {
  for (const fill of scope.querySelectorAll<HTMLElement>('[data-target]')) {
    fill.style.width = fill.dataset.target ?? '0%';
  }
}

function arsenalSection(): HTMLElement {
  const section = el('section', `${SECTION} bg-bg`);
  section.id = 'arsenal';
  const guns = arsenalWeapons();
  section.append(sectionHead('armoury', t('landing.arsenal.title', { count: guns.length }), t('landing.arsenal.lede'), 'right'));

  const maxDamage = Math.max(...guns.map((g) => g.damage));
  const maxRpm = Math.max(...guns.map((g) => g.rpm));
  const maxRange = Math.max(...guns.map((g) => g.rangeM));

  const strip = el('div', 'flex gap-4 overflow-x-auto snap-x snap-mandatory pt-2 pb-5 -mx-5 px-5 sm:-mx-8 sm:px-8 lg:-mx-14 lg:px-14 xl:-mx-24 xl:px-24 [scrollbar-width:thin]');
  strip.dataset.slot = 'arsenal-strip';
  guns.forEach((gun, index) => {
    const card = el('article', `reveal tilt-card snap-start shrink-0 w-[280px] sm:w-[320px] kit-plate [--kp-rim:20px] [--kp-pad:22px_20px] flex flex-col gap-3 group [transition-delay:${Math.min(index, 4) * 70}ms]`);
    card.dataset.bars = 'true';
    card.dataset.weapon = String(gun.id);
    // The stills are gunmetal; the well they sit in is the dark recess that gives the eye an edge.
    const figure = el('div', 'kit-well relative h-[150px] flex items-center justify-center');
    const img = el('img', 'relative max-h-[86%] max-w-[92%] object-contain brightness-125 contrast-110 drop-shadow-[0_12px_16px_rgba(0,0,0,0.8)] transition-transform duration-300 group-hover:scale-[1.08] group-hover:-rotate-2');
    img.src = `/game/ui/weapons/${gun.id}.png`;
    img.alt = gun.name;
    img.loading = 'lazy';
    img.decoding = 'async';
    figure.append(img);
    const name = el('h3', 'kit-title font-heading uppercase text-[22px] m-0', gun.name);
    const meta = el('p', 'font-heading uppercase tracking-[0.16em] text-[11px] text-text-dim m-0', t('landing.arsenal.magazine', { rounds: gun.magazine, seconds: (gun.reloadMs / 1000).toFixed(1) }));
    const bars = el('div', 'flex flex-col gap-1');
    bars.append(
      statBar('kills', t('landing.arsenal.damage'), gun.damage, maxDamage, String(gun.damage), 0),
      statBar('streak', t('landing.arsenal.rate'), gun.rpm, maxRpm, t('landing.arsenal.rpm', { value: gun.rpm }), 120),
      statBar('headshot', t('landing.arsenal.range'), gun.rangeM, maxRange, t('landing.arsenal.metres', { value: gun.rangeM }), 240),
    );
    card.append(figure, name, meta, bars);
    attachTilt(card, 6);
    strip.append(card);
  });
  section.append(strip);
  return section;
}

// ── Maps ────────────────────────────────────────────────────────────────────────────────────

/** Every shipped map except the greybox, which is a test fixture and says so in its own description. */
export function landingMaps(): MapDefinition[] {
  return Object.values(CLIENT_MAPS)
    .map((map) => map.definition)
    .filter((map) => map.name.startsWith('blackline-'));
}

function mapsSection(): HTMLElement {
  const section = el('section', `${SECTION} bg-bg`);
  section.id = 'maps';
  const maps = landingMaps();
  section.append(sectionHead('play', t('landing.maps.title', { count: maps.length }), t('landing.maps.lede')));
  const grid = el('div', 'grid gap-5 sm:grid-cols-2 xl:grid-cols-3');
  // Every tile is the same tile. A double-width "featured" card for the default-rotation map
  // read as a hole in the grid whichever way it was sized: stretched to its row it letterboxed
  // its own aerial, sized to its content (`sm:self-start`) it left the row's surplus as an empty
  // band under it — reported as a defect both times. Eight maps on three columns close as
  // 3 / 3 / 2, which is an ordinary short last row and not a gap under anything. The
  // default-rotation map is still first and still says so, in a label rather than in the layout.
  maps.forEach((map, index) => {
    const card = el('article', `reveal kit-plate [--kp-rim:20px] [--kp-pad:20px_18px] flex flex-col gap-3 [transition-delay:${(index % 3) * 90}ms]`);
    card.dataset.map = map.name;
    // The card image is a PHOTOGRAPH of the level — the arena renderer's own three-quarter
    // aerial (`tools/ui-stills/render-map-previews.mjs` → `/game/ui/maps/<name>.jpg`), with
    // the blueprint plan tucked into a corner as the map's "minimap" (2026-09-09: rendered
    // previews instead of minimaps). A map without a rendered still yet falls back to the
    // full-size plan, so a new generator never ships a broken image.
    // A real 16:10 on every plan, never `aspect-auto`: the aerial is the one shot on the page
    // where the whole level is supposed to be legible, and a plan that takes its height from a
    // row crops it to a near-square (measured 413x434 at 1440px).
    const plan = el('div', 'bp-plan kit-well relative aspect-[16/10] overflow-hidden');
    plan.dataset.plan = 'true';
    const photo = document.createElement('img');
    photo.src = `/game/ui/maps/${map.name}.jpg`;
    photo.alt = t('landing.maps.aerialAlt', { name: map.displayName });
    photo.loading = 'lazy';
    photo.decoding = 'async';
    photo.className = 'absolute inset-0 w-full h-full object-cover';
    // The blueprint starts FULL-BLEED and shrinks to the corner once the photo is really on
    // screen. Drawn the other way round — corner first, full-bleed only on `error` — a lazy
    // photo left the tile a black rectangle with a small grid in it for the whole time between
    // paint and decode, which on a slow line is most of the visit and reads as a missing
    // preview (reported as a map with no preview; all eight files return 200). The interim state
    // has to read as a map plan, so the plan is the backdrop and the photo is what arrives.
    const inset = el('div', 'absolute inset-0 [&>svg]:w-full [&>svg]:h-full');
    inset.dataset.inset = 'true';
    inset.innerHTML = blueprintSvg(map, { size: 320 });
    const toCorner = (): void => {
      inset.className = 'kit-well absolute right-3 bottom-3 w-[34%] aspect-square [&>svg]:w-full [&>svg]:h-full';
    };
    // Both paths, because either can be the only one that happens: `decode()` waits for pixels
    // rather than bytes (no flash of an undecoded image under a shrinking plan), and a cached
    // image can be `complete` before this line runs and never fire `load` at all.
    const onLoaded = (): void => {
      if (typeof photo.decode === 'function') void photo.decode().then(toCorner, toCorner);
      else toCorner();
    };
    photo.addEventListener('load', onLoaded);
    // No photo at all (a generator that shipped before its still): the plan simply stays
    // full-bleed, which is the same fallback as before by a shorter route.
    photo.addEventListener('error', () => { photo.remove(); });
    if (photo.complete && photo.naturalWidth > 0) onLoaded();
    plan.append(photo, inset);
    const { width, depth } = mapFootprintMeters(map);
    const corner = el('div', 'kit-chip [--kh:24px] absolute top-2 left-2 font-[ui-monospace,Menlo,monospace] text-[10px] uppercase tracking-[0.1em]');
    corner.textContent = map.sites.length
      ? t('landing.maps.footprintSites', { width, depth, spawns: map.spawns.length, sites: map.sites.length })
      : t('landing.maps.footprint', { width, depth, spawns: map.spawns.length });
    plan.append(corner);
    // `grow` + `mt-auto` on the tags: a grid row is as tall as its tallest card, and the shorter
    // ones stretch. The surplus goes under the body's own background with the tag row pinned to
    // the bottom edge, so a stretched card reads as a card and not as a card with a hole.
    const body = el('div', 'flex flex-col gap-2 grow');
    body.append(
      el('h3', 'kit-title font-heading uppercase m-0 text-[22px]', map.displayName),
      // Three lines on every tile: one description running longer than its neighbours' is what
      // stretches the row, and every card in that row then pays for it.
      el('p', 'text-[14px] leading-relaxed text-text-dim m-0 [display:-webkit-box] [-webkit-box-orient:vertical] overflow-hidden [-webkit-line-clamp:3]', map.description),
    );
    const tags = el('div', 'flex flex-wrap gap-2 pt-1 mt-auto');
    const tag = (art: IconName, text: string): HTMLElement => {
      const chip = el('span', 'kit-chip [--kh:30px] font-heading uppercase tracking-[0.1em] text-[11px]');
      chip.append(icon(art, { size: 24 }), text);
      return chip;
    };
    if (index === 0) tags.append(tag('star', t('landing.maps.defaultRotation')));
    tags.append(map.sites.length ? tag('bomb', t('landing.maps.competitive')) : tag('play', t('landing.maps.deathmatch')));
    body.append(tags);
    card.append(plan, body);
    grid.append(card);
  });
  section.append(grid);
  return section;
}

// ── Operators ───────────────────────────────────────────────────────────────────────────────

export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

/** Presentation tier from how hard a skin is to get — the catalogue itself has no rarity. */
export function skinRarity(skin: SkinDefinition): Rarity {
  switch (skin.unlock.kind) {
    case 'default': return 'common';
    case 'purchase': return skin.unlock.gemCost >= 2750 ? 'epic' : 'rare';
    case 'level': return skin.unlock.level >= 10 ? 'epic' : 'rare';
    case 'rank': return 'legendary';
  }
}

/**
 * How a skin is earned, in the visitor's language. `describeUnlock` in `shared/` returns English
 * prose, which is fine for a server log and wrong for a card a Norwegian reads — the shape of the
 * unlock is the shared fact, the sentence about it is ours.
 */
export function unlockLabel(unlock: SkinDefinition['unlock']): string {
  switch (unlock.kind) {
    case 'default': return t('landing.operators.unlock.owned');
    // The gem alternative is part of the sentence, not a footnote: every skin is on the shelf,
    // and a level or rank gate is the FREE route to the same body rather than the only one
    // (`shared/src/cosmetics.ts`'s `skinGemCost`). Saying only "Level 30" tells a visitor the
    // operator is locked behind play, which is not true of any of them.
    case 'level': return t('landing.operators.unlock.level', { level: unlock.level, gems: unlock.gemCost });
    case 'rank': return t('landing.operators.unlock.rank', { points: unlock.points, gems: unlock.gemCost });
    case 'purchase': return t('landing.operators.unlock.purchase', { gems: unlock.gemCost });
  }
}

const UNLOCK_ART: Record<SkinDefinition['unlock']['kind'], IconName> = { default: 'done', level: 'xp', rank: 'rank', purchase: 'premium' };

function operatorsSection(): HTMLElement {
  const section = el('section', `${SECTION} bg-bg`);
  section.id = 'operators';
  section.append(sectionHead('squad', t('landing.operators.title', { count: SKIN_LIST.length }), t('landing.operators.lede')));
  const strip = el('div', 'flex gap-4 overflow-x-auto snap-x snap-mandatory pt-2 pb-5 -mx-5 px-5 sm:-mx-8 sm:px-8 lg:-mx-14 lg:px-14 xl:-mx-24 xl:px-24 [scrollbar-width:thin]');
  strip.dataset.slot = 'operators-strip';
  for (const skin of SKIN_LIST) {
    const rarity = skinRarity(skin);
    const card = el('article', `reveal snap-start shrink-0 w-[200px] sm:w-[220px] kit-plate [--kp-rim:16px] [--kp-pad:16px_14px] flex flex-col gap-2 group transition-transform duration-300 hover:-translate-y-1.5`);
    card.dataset.skin = String(skin.id);
    // The rarity colour lives on the picture's backing, not on the frame.
    const figure = el('div', `relative aspect-[3/4] flex items-end justify-center overflow-hidden border rarity-${rarity}`);
    const img = el('img', 'max-h-full object-contain drop-shadow-[0_14px_16px_rgba(0,0,0,0.75)] transition-transform duration-300 group-hover:scale-[1.05]');
    img.src = `/game/ui/skins/${skin.id}.png`;
    img.alt = skin.name;
    img.loading = 'lazy';
    img.decoding = 'async';
    figure.append(img);
    const name = el('h3', 'kit-title font-heading uppercase text-[17px] m-0 truncate', skin.name);
    const unlock = el('p', 'flex items-center gap-1.5 font-heading uppercase tracking-[0.1em] text-[10px] text-text-dim m-0');
    unlock.append(icon(UNLOCK_ART[skin.unlock.kind], { size: 24 }), unlockLabel(skin.unlock));
    card.append(figure, name, unlock);
    strip.append(card);
  }
  section.append(strip);
  return section;
}

// ── Under the hood ──────────────────────────────────────────────────────────────────────────

const NETCODE_ART: IconName[] = ['time', 'streak', 'prev', 'next'];

function netcodeSection(): HTMLElement {
  const section = el('section', `${SECTION} bg-bg`);
  section.id = 'netcode';
  const wrap = el('div', 'grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-center');
  const copy = el('div', 'reveal flex flex-col gap-5');
  copy.append(titlePlate('time', el('h2', H2, t('landing.netcode.title'))), el('p', LEDE, t('landing.netcode.lede')));
  const tiles = el('div', 'grid grid-cols-2 gap-4');
  NETCODE_FACTS.forEach((fact, index) => {
    const tile = el('div', `reveal kit-plate [--kp-rim:18px] [--kp-pad:20px_18px] flex flex-col gap-2 min-h-[170px] [transition-delay:${index * 80}ms]`);
    const figure = el('div', 'flex items-center gap-2');
    const number = el('span', 'kit-title font-heading text-[44px] leading-none tabular-nums', '0');
    number.dataset.countTo = fact.value;
    figure.append(icon(NETCODE_ART[index] ?? 'time', { size: 40, className: 'kit-float' }), number, el('span', 'kit-label font-heading uppercase text-[15px]', fact.unit));
    tile.append(figure, el('p', 'kit-label font-heading uppercase text-[12px] m-0', t(fact.labelKey)), el('p', 'text-[13px] leading-snug text-text-dim m-0', t(fact.noteKey)));
    tiles.append(tile);
  });
  wrap.append(copy, tiles);
  section.append(wrap);
  return section;
}

// ── Questions ───────────────────────────────────────────────────────────────────────────────

/**
 * One row of `GET /cms/faq` — the `FaqEntry` model as the public controller returns it
 * (`active: true`, already ordered by `sortOrder`). There is no `category` column, so this
 * section does not group; the admin's ordering is the only editorial control there is.
 */
export interface FaqItem {
  id: string;
  question: string;
  answer: string;
  sortOrder?: number;
}

/** How many questions the landing carries before it defers to /faq. */
export const FAQ_LANDING_LIMIT = 8;

function faqSection(navigate: Navigate): HTMLElement {
  const section = el('section', `${SECTION} bg-bg`);
  section.id = 'faq';
  // Hidden until the CMS answers, and removed if it answers with nothing: an empty accordion
  // under a heading reads as a broken page.
  section.hidden = true;
  section.append(sectionHead('bell', t('landing.faq.title'), t('landing.faq.lede')));

  const list = el('div', 'flex flex-col gap-3 max-w-[900px]');
  list.dataset.slot = 'faq-list';
  // No `reveal` on the accordion or this link: they are appended after the reveal observer has
  // already taken its census, and an unobserved `.reveal` stays at opacity 0 forever.
  const more = el('div', 'pt-7');
  more.dataset.slot = 'faq-more';
  more.hidden = true;
  more.append(plateLink('/faq', ctaLabel('next', t('landing.faq.seeAll'), 24), { variant: 'secondary', size: 'md', className: 'text-[12px] tracking-[0.14em]' }, navigate));
  section.append(list, more);

  const rows: { toggle: HTMLButtonElement; panel: HTMLElement; mark: HTMLElement }[] = [];
  const setOpen = (index: number, open: boolean): void => {
    const row = rows[index];
    if (!row) return;
    row.toggle.setAttribute('aria-expanded', String(open));
    row.panel.hidden = !open;
    // The painted + turns into a × when the row is open, so the key says what a press does.
    row.mark.classList.toggle('rotate-45', open);
  };

  void api.get<FaqItem[]>('/cms/faq')
    .then((items) => {
      if (!Array.isArray(items) || items.length === 0) {
        section.remove();
        return;
      }
      items.slice(0, FAQ_LANDING_LIMIT).forEach((item, index) => {
        const row = el('div', 'kit-plate [--kp-rim:16px] [--kp-pad:6px_14px] kit-deal');
        row.style.setProperty('--i', String(index));
        row.dataset.faq = String(item.id);
        const panelId = `faq-answer-${index}`;
        const toggleId = `faq-question-${index}`;
        const toggle = el('button', 'w-full flex items-center justify-between gap-4 text-left min-h-[56px] py-2 bg-transparent border-0 cursor-pointer touch-manipulation kit-label font-heading uppercase text-[13px] sm:text-[15px]');
        toggle.type = 'button';
        toggle.id = toggleId;
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', panelId);
        const mark = el('span', 'shrink-0 transition-transform duration-200 motion-reduce:transition-none');
        mark.setAttribute('aria-hidden', 'true');
        mark.append(icon('add', { size: 40 }));
        toggle.append(el('span', 'flex-1', item.question), mark);
        // Answers are admin-supplied text and land as text — never innerHTML.
        const panel = el('div', 'kit-well mb-2 px-4 py-3 text-[14px] leading-relaxed text-text whitespace-pre-line');
        panel.id = panelId;
        panel.hidden = true;
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-labelledby', toggleId);
        panel.textContent = item.answer;
        // A native button: Enter and Space already work, and one panel is open at a time.
        toggle.addEventListener('click', () => {
          const wasOpen = toggle.getAttribute('aria-expanded') === 'true';
          for (let i = 0; i < rows.length; i += 1) setOpen(i, false);
          if (!wasOpen) setOpen(index, true);
        });
        row.append(toggle, panel);
        rows.push({ toggle, panel, mark });
        list.append(row);
      });
      more.hidden = items.length <= FAQ_LANDING_LIMIT;
      section.hidden = false;
    })
    .catch(() => { section.remove(); });

  return section;
}

// ── Closing CTA ─────────────────────────────────────────────────────────────────────────────

function closingSection(navigate: Navigate): HTMLElement {
  const section = el('section', 'relative px-5 sm:px-8 lg:px-14 xl:px-24 py-28 lg:py-40 overflow-hidden');
  section.id = 'play';
  const art = el('img', 'absolute inset-0 w-full h-full object-cover opacity-60 will-change-transform scale-[1.12]');
  art.dataset.slot = 'closing-art';
  art.src = '/game/ui/loading-keyart.jpg';
  art.alt = '';
  art.loading = 'lazy';
  section.append(
    art,
    el('div', 'absolute inset-0 bg-[linear-gradient(180deg,rgba(10,11,13,1)_0%,rgba(10,11,13,0.55)_35%,rgba(10,11,13,0.55)_65%,rgba(10,11,13,1)_100%)]'),
  );
  const inner = el('div', 'reveal relative z-10 kit-window kit-pop mx-auto max-w-[640px] flex flex-col items-center text-center gap-5');
  inner.append(
    titlePlate('trophy', el('h2', H2, t('landing.closing.title'))),
    el('p', `${LEDE} text-center`, t('landing.closing.lede')),
  );
  const ctas = el('div', 'flex flex-wrap justify-center gap-3 pt-1');
  ctas.append(
    plateLink('/register', ctaLabel('play', t('landing.closing.createAccount'), 32, true), CTA_PRIMARY, navigate),
    plateLink('/login', ctaLabel('shield', t('landing.closing.haveOne')), CTA_GHOST, navigate),
  );
  inner.append(ctas);
  section.append(inner);
  return section;
}

// ── Footer ──────────────────────────────────────────────────────────────────────────────────

interface SocialLink { platform: string; url: string; icon: string }

/**
 * The shared footer. `navigate` is optional because the content pages import this by name and
 * call it bare; without it the content links fall back to plain anchors, which `main.ts`'s
 * document-level interceptor still routes in-place — either way there is no full page load.
 */
export function landingFooter(navigate?: Navigate): HTMLElement {
  // The painted strip: one armored window across the page's foot, its columns set into it.
  const foot = el('footer', 'relative px-3 sm:px-6 lg:px-10 pt-6 pb-8 text-[13px] text-text-dim');
  const strip = el('div', 'kit-window');
  const grid = el('div', 'grid gap-8 sm:grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_1fr]');

  const brand = el('div', 'flex flex-col gap-4');
  // `self-start`: the brand column is a flex column, so an `inline-block` span becomes a flex
  // item and stretches to the column's width — which the wordmark's absolute strike bar then
  // spans, drawing a teal line halfway across the footer. It hugs its text everywhere else
  // because those parents are `inline-flex`.
  brand.append(wordmark('text-[34px] self-start'));
  const company = el('p', 'm-0 leading-relaxed');
  company.dataset.slot = 'footer-company';
  company.textContent = t('landing.footer.about');
  brand.append(company);
  // The networks, as a row of icon keys under the name. Filled from the CMS below; empty (and
  // invisible) until then.
  const socialRow = el('ul', 'flex flex-wrap gap-2 m-0 p-0 list-none empty:hidden');
  socialRow.dataset.slot = 'footer-social';
  brand.append(socialRow);

  const legal = el('div', 'flex flex-col gap-3');
  legal.append(el('h4', 'kit-label font-heading uppercase text-[12px] m-0', t('landing.footer.legal')));
  const legalList = el('ul', 'flex flex-col items-start gap-2 m-0 p-0 list-none');
  legalList.dataset.slot = 'footer-legal';
  legal.append(legalList);

  const contact = el('div', 'flex flex-col gap-3');
  contact.append(el('h4', 'kit-label font-heading uppercase text-[12px] m-0', t('landing.footer.contact')));
  const contactList = el('ul', 'flex flex-col items-start gap-2 m-0 p-0 list-none');
  contactList.dataset.slot = 'footer-contact';
  contact.append(contactList);

  const explore = el('div', 'flex flex-col gap-3');
  explore.append(el('h4', 'kit-label font-heading uppercase text-[12px] m-0', t('nav.explore')));
  const exploreList = el('ul', 'flex flex-col items-start gap-2 m-0 p-0 list-none');
  exploreList.dataset.slot = 'footer-explore';
  explore.append(exploreList);

  grid.append(brand, explore, legal, contact);
  strip.append(grid);
  foot.append(strip);

  let companyInfo: CompanyInfo | null = null;

  const LINK = 'kit-well text-text hover:text-cyan cursor-pointer border-0 px-3 font-inherit min-h-[44px] inline-flex items-center touch-manipulation';

  for (const [href, label] of navRoutes()) {
    const li = el('li');
    li.append(navigate ? link(href, label, LINK, navigate) : (() => {
      const a = el('a', LINK, label);
      a.href = href;
      return a;
    })());
    exploreList.append(li);
  }

  const openLegal = async (page: LegalPageMeta): Promise<void> => {
    let close: () => void = () => {};
    const body = el('div', 'flex flex-col gap-3 text-[14px] leading-relaxed text-text [&_h2]:font-heading [&_h2]:uppercase [&_h2]:text-[18px] [&_h2]:mt-4 [&_a]:text-cyan [&_ul]:pl-5 [&_li]:list-disc');
    body.textContent = t('common.loading');
    close = openModal({ title: page.title, content: [body], onClose: () => close(), width: 'lg' });
    try {
      const doc = await fetchLegalPage(page.slug);
      body.innerHTML = fillCompanyVars(doc.content, companyInfo);
    } catch {
      body.textContent = t('landing.footer.docFailed');
    }
  };

  // Everything below is best-effort: a footer that fails to fetch stays a footer.
  void fetchCompanyInfo()
    .then((info) => {
      companyInfo = info;
      const parts = [info.name, info.registrationNumber && t('landing.footer.registration', { number: info.registrationNumber }), info.address, info.jurisdiction].filter(Boolean);
      if (parts.length) company.textContent = t('landing.footer.operatedBy', { game: info.gameName || 'Black Line', parts: parts.join(' · ') });
      if (info.email) {
        const li = el('li');
        const a = el('a', LINK, info.email);
        a.href = `mailto:${info.email}`;
        li.append(a);
        contactList.prepend(li);
      }
    })
    .catch(() => {});
  void fetchLegalPages()
    .then((pages) => {
      for (const page of sortLegalPages(pages)) {
        const li = el('li');
        const btn = el('button', LINK, page.title);
        btn.type = 'button';
        btn.addEventListener('click', () => { void openLegal(page); });
        li.append(btn);
        legalList.append(li);
      }
    })
    .catch(() => {});
  void api.get<SocialLink[]>('/cms/social-links')
    .then((links) => {
      for (const social of links) {
        if (!social.url || social.url === '#') continue;
        const li = el('li');
        const key = socialPlatformKey(social);
        if (key) {
          // A known network is its icon on a square painted key, named for screen readers and on hover.
          const a = document.createElement('a');
          const img = el('img', 'w-5 h-5 select-none');
          img.src = socialIconUrl(key);
          img.alt = '';
          img.draggable = false;
          plateChrome(a, img, { variant: 'secondary', size: 'md', className: 'plate-icon no-underline' });
          a.href = social.url;
          a.target = '_blank';
          a.rel = 'noopener';
          a.title = social.platform;
          a.setAttribute('aria-label', social.platform);
          li.append(a);
          socialRow.append(li);
          continue;
        }
        // Anything else stays a painted text key with the contacts.
        const a = document.createElement('a');
        plateChrome(a, social.platform, { variant: 'secondary', size: 'md', className: 'capitalize no-underline text-[12px]' });
        a.href = social.url;
        a.target = '_blank';
        a.rel = 'noopener';
        li.append(a);
        contactList.append(li);
      }
    })
    .catch(() => {});

  return foot;
}

// ── Top bar ─────────────────────────────────────────────────────────────────────────────────

/**
 * The chrome every landing-family page wears. Exported for /faq, /news and /guide, which is why
 * nothing in here reads the landing's own DOM except through `sectionAnchor`'s fallback.
 */
export function landingTopBar(navigate: Navigate): HTMLElement {
  const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  const bar = el('header', 'fixed inset-x-0 top-0 z-30 flex flex-col px-2 sm:px-4 pt-[max(8px,env(safe-area-inset-top))]');
  bar.dataset.slot = 'landing-topbar';
  // The painted strip (`landing-strip`, app.css): header art stretched across the page.
  const row = el('div', 'landing-strip flex items-center justify-between gap-2 [--kh:56px]');

  const home = el('a', 'inline-flex items-center min-h-[44px] no-underline');
  home.href = '/';
  home.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    // On the landing this is "back to the top"; on a content page it is a route home.
    if (document.querySelector('[data-view="landing"]')) window.scrollTo({ top: 0, behavior: 'smooth' });
    else navigate('/');
  });
  home.append(wordmark('text-[26px] sm:text-[30px]'));

  // Painted nav keys need room, so the inline nav starts at 1600px; below it the burger carries
  // the same links. At `xl` (1280) eight keys plus the CTAs shrank their words to 11px and read
  // as noise (2026-10-12).
  const nav = el('nav', 'hidden min-[1600px]:flex items-center gap-1.5 font-heading uppercase tracking-[0.05em] text-[13px]');
  nav.setAttribute('aria-label', t('nav.sections'));
  const NAV_LINK = 'kit-tab [--kh:40px] px-3 no-underline';
  for (const [href, label] of navSections()) nav.append(sectionAnchor(href, label, NAV_LINK, navigate));
  for (const [href, label] of navRoutes()) nav.append(link(href, label, `${NAV_LINK} first:ml-3`, navigate));

  // The picker itself is `engine/i18n/lang-pick.ts` and the sound switch is
  // `engine/shell/sound-toggle.ts`; the bar only reserves them seats, because both hold
  // listeners that need a disposer and this function returns an element. `main.ts` fills them.
  // Under 640px the picker moves to the drawer too, for the reason the sound toggle already
  // did: with the body face now set in Oswald the bar's own CTA reads wider, and a wordmark, a
  // picker, a toggle, a CTA and a burger stopped fitting on a 390px row — "PLAY FREE" wrapped
  // to two lines and the plate grew with it. Both tools have a full-width copy in the drawer.
  const langSlot = el('div', 'hidden sm:flex items-center');
  langSlot.dataset.slot = 'lang-pick';
  // Hidden under 640px like the "Sign in" link beside it: at 320px the row is already a
  // wordmark, a picker, a CTA and a burger, and a fifth 44px plate is what pushes it into a
  // horizontal scroll. The drawer's copy is the phone's control.
  const soundSlot = el('div', 'hidden sm:flex items-center');
  soundSlot.dataset.slot = 'sound-toggle';

  const drawerId = 'landing-drawer';
  const burger = document.createElement('button');
  burger.type = 'button';
  burger.dataset.slot = 'nav-burger';
  burger.setAttribute('aria-label', t('nav.menu'));
  burger.setAttribute('aria-expanded', 'false');
  burger.setAttribute('aria-controls', drawerId);
  const strokes = document.createElement('span');
  strokes.className = 'flex flex-col gap-[5px]';
  for (let i = 0; i < 3; i += 1) {
    const stroke = el('span', 'block w-[18px] h-[3px] rounded-full bg-white shadow-[0_1px_0_var(--kit-ink)]');
    stroke.setAttribute('aria-hidden', 'true');
    strokes.append(stroke);
  }
  plateChrome(burger, strokes, { variant: 'secondary', size: 'md', className: 'plate-icon' });
  const burgerSeat = el('div', 'min-[1600px]:hidden flex');
  burgerSeat.append(burger);

  // Signed in, the two-CTA cluster is wrong twice over: "Sign in" is a dead end and "Play free"
  // pitches an account the visitor already has. The content pages (/faq, /news, /guide) are
  // public and a player reaches them from inside the game, so this bar is not only ever seen by
  // strangers — one plate straight back to the menu is the whole difference.
  const signedIn = authStore.get().user !== null;

  const signInSeat = el('div', 'hidden min-[1600px]:flex');
  signInSeat.append(plateLink('/login', ctaLabel('shield', t('common.signIn'), 24), { variant: 'secondary', size: 'md', className: 'text-[12px] tracking-[0.14em] whitespace-nowrap' }, navigate));

  const actions = el('div', 'flex items-center gap-2');
  // Sound before language: the music starts one click into the site, so the switch for it is
  // the first control a visitor should find.
  actions.append(soundSlot, langSlot);
  if (signedIn) {
    actions.append(plateLink('/play', ctaLabel('play', t('nav.play'), 24), { variant: 'gold', size: 'md', className: 'text-[12px] tracking-[0.14em]' }, navigate));
  } else {
    actions.append(
      // Sign in moves into the drawer on a phone; at 320px the bar has room for one CTA.
      signInSeat,
      // `whitespace-nowrap`: the plate's height comes from `--pk-h`, so a label that wraps
      // does not grow the plate — it overflows it.
      plateLink('/register', t('landing.hero.playFree'), { variant: 'gold', size: 'md', className: 'text-[12px] tracking-[0.14em] whitespace-nowrap' }, navigate),
    );
  }
  actions.append(burgerSeat);
  row.append(home, nav, actions);

  // ── The drawer ────────────────────────────────────────────────────────────────────────────
  const drawer = el('nav', 'xl:hidden mx-1 mt-3 overflow-hidden opacity-0 kit-window [--kw-rim:16px] [--kw-pad:10px] transition-[max-height,opacity] duration-200 ease-out motion-reduce:transition-none');
  drawer.id = drawerId;
  drawer.dataset.slot = 'nav-drawer';
  drawer.setAttribute('aria-label', t('nav.menu'));
  drawer.hidden = true;
  drawer.style.maxHeight = '0px';
  const inner = el('div', 'flex flex-col gap-2 max-h-[70dvh] overflow-y-auto pb-[max(8px,env(safe-area-inset-bottom))]');
  drawer.append(inner);

  const drawerKey = (a: HTMLAnchorElement, variant: 'secondary' | 'primary' = 'secondary'): HTMLAnchorElement => {
    plateChrome(a, a.textContent ?? '', { variant, size: 'md', className: 'w-full justify-start no-underline text-[13px] tracking-[0.14em]' });
    return a;
  };

  let open = false;
  let detach = (): void => {};
  const setOpen = (next: boolean): void => {
    open = next;
    burger.setAttribute('aria-expanded', String(next));
    if (next) {
      drawer.hidden = false;
      // One frame of the collapsed height first, so there is something to animate from.
      const grow = (): void => { drawer.style.maxHeight = '70dvh'; drawer.style.opacity = '1'; };
      if (reducedMotion || typeof requestAnimationFrame !== 'function') grow();
      else requestAnimationFrame(grow);
      attach();
    } else {
      drawer.style.maxHeight = '0px';
      drawer.style.opacity = '0';
      // Closed means gone, not merely flat: a zero-height drawer still holds focusable links.
      drawer.hidden = true;
      detach();
    }
  };
  const close = (): void => { if (open) setOpen(false); };

  // Document listeners exist only while the drawer is open, and self-remove if the bar is torn
  // down mid-open — `landingTopBar` returns an element, not a disposer, so it cannot rely on one.
  const onDocPointerDown = (event: Event): void => {
    if (!bar.isConnected) { detach(); return; }
    if (!bar.contains(event.target as Node)) close();
  };
  const onDocKeyDown = (event: KeyboardEvent): void => {
    if (!bar.isConnected) { detach(); return; }
    if (event.key === 'Escape') { close(); burger.focus(); }
  };
  const attach = (): void => {
    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onDocKeyDown);
  };
  detach = (): void => {
    document.removeEventListener('pointerdown', onDocPointerDown, true);
    document.removeEventListener('keydown', onDocKeyDown);
  };
  burger.addEventListener('click', (event) => { event.stopPropagation(); setOpen(!open); });

  for (const [href, label] of navSections()) inner.append(drawerKey(sectionAnchor(href, label, '', navigate, close)));
  for (const [href, label] of navRoutes()) {
    const a = drawerKey(link(href, label, '', navigate));
    a.addEventListener('click', close);
    inner.append(a);
  }
  const account = signedIn
    ? drawerKey(link('/play', t('nav.backToMenu'), '', navigate), 'primary')
    : drawerKey(link('/login', t('common.signIn'), '', navigate), 'primary');
  account.addEventListener('click', close);
  inner.append(account);

  // The drawer's tool row: the same two controls, stacked, because the bar's copies are behind
  // the burger under 768px and a mute button you cannot reach is not a mute button.
  const tools = el('div', 'flex flex-col gap-2 mt-1');
  const soundSlotMobile = el('div', 'flex items-center');
  soundSlotMobile.dataset.slot = 'sound-toggle-mobile';
  const langSlotMobile = el('div', 'flex items-center');
  langSlotMobile.dataset.slot = 'lang-pick-mobile';
  tools.append(soundSlotMobile, langSlotMobile);
  inner.append(tools);

  bar.append(row, drawer);
  return bar;
}

/** Counts a figure up from zero over ~900ms with an ease-out; instant under reduced motion. */
function countUp(node: HTMLElement, instant: boolean): void {
  const target = Number(node.dataset.countTo ?? '0');
  if (instant || !Number.isFinite(target) || typeof requestAnimationFrame !== 'function') {
    node.textContent = String(target);
    return;
  }
  const started = performance.now();
  const step = (now: number): void => {
    const t = Math.min(1, (now - started) / 900);
    const eased = 1 - Math.pow(1 - t, 3);
    node.textContent = String(Math.round(target * eased));
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

// ── Mount ───────────────────────────────────────────────────────────────────────────────────

/**
 * What `renderLanding` hands back.
 *
 * A handle rather than a bare disposer because the boot splash has to know when the hero has a
 * frame: on `/` the page is revealed complete, and the only thing that can say when that is
 * true is the scene itself (`landing-scene.ts`'s ready gate), threaded up through `mountHero`.
 */
export interface LandingHandle {
  dispose(): void;
  /** Resolves once the hero is showing either a rendered frame or the key art. Never rejects. */
  ready: Promise<void>;
  /**
   * Puts the rendered still into the hero, for a caller that has given up waiting on `ready`.
   *
   * Only the boot's cap path calls it (`main.ts`). It exists because the still is a *different
   * picture* from the live hero — a lobby shot against an aerial — so it must not be sitting
   * under a scene that is about to appear; something has to decide there will be no scene, and
   * that decision belongs to whoever owns the timeout.
   */
  showStill(): void;
}

export function renderLanding(app: HTMLElement, navigate: Navigate): LandingHandle {
  const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  const root = el('div', 'landing relative min-h-full bg-bg text-text overflow-x-hidden');
  root.dataset.view = 'landing';
  const { node: hero, scene, ready, showStill } = mountHero(navigate, reducedMotion);
  root.append(
    landingTopBar(navigate),
    hero,
    modesSection(),
    arsenalSection(),
    mapsSection(),
    operatorsSection(),
    netcodeSection(),
    faqSection(navigate),
    closingSection(navigate),
    landingFooter(navigate),
  );
  app.replaceChildren(root);
  try { window.scrollTo(0, 0); } catch { /* jsdom */ }

  // Scroll reveal. Instant when the observer is missing (jsdom, ancient browsers) or when the
  // visitor asked for reduced motion — a reveal that never fires is a page with missing content.
  const revealables = Array.from(root.querySelectorAll<HTMLElement>('.reveal'));
  const planObservers: IntersectionObserver[] = [];
  /** Per-element effects that should start the moment the element is seen, once. */
  const onReveal = (node: HTMLElement): void => {
    if (node.dataset.bars) requestAnimationFrame(() => fillBars(node));
    for (const counter of node.querySelectorAll<HTMLElement>('[data-count-to]')) countUp(counter, reducedMotion);
  };
  let observer: IntersectionObserver | null = null;
  if (!reducedMotion && typeof IntersectionObserver === 'function') {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            onReveal(entry.target as HTMLElement);
            observer?.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 },
    );
    for (const node of revealables) observer.observe(node);
  } else {
    for (const node of revealables) {
      node.classList.add('is-in');
      onReveal(node);
    }
  }
  // Plans plot themselves in too — they sit inside a revealed card but run their own timeline.
  for (const plan of root.querySelectorAll<HTMLElement>('[data-plan]')) {
    const card = plan.closest('.reveal');
    if (!observer || !card) plan.classList.add('is-in');
    else {
      const planObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) { plan.classList.add('is-in'); planObserver.disconnect(); }
        }
      }, { threshold: 0.2 });
      planObserver.observe(plan);
      planObservers.push(planObserver);
    }
  }

  // The closing key art drifts slower than the page — a real parallax, one transform per frame.
  const closingArt = root.querySelector<HTMLElement>('[data-slot="closing-art"]');
  let parallaxFrame = 0;
  const onParallax = (): void => {
    if (!closingArt || reducedMotion) return;
    if (parallaxFrame) return;
    parallaxFrame = requestAnimationFrame(() => {
      parallaxFrame = 0;
      const rect = closingArt.parentElement!.getBoundingClientRect();
      const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      closingArt.style.transform = `translateY(${((progress - 0.5) * 120).toFixed(1)}px) scale(1.12)`;
    });
  };
  window.addEventListener('scroll', onParallax, { passive: true });
  onParallax();

  return {
    ready,
    showStill,
    dispose(): void {
      observer?.disconnect();
      for (const planObserver of planObservers) planObserver.disconnect();
      window.removeEventListener('scroll', onParallax);
      if (parallaxFrame) cancelAnimationFrame(parallaxFrame);
      scene?.dispose();
      root.remove();
    },
  };
}
